import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import type {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import type {SurveyTemplateDefinition} from "crqlar-react-common/dist/models/survey/surveyTemplate.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {useSurveyChangeRefresh} from "../shared/surveyQueryRefresh";
import {resolveSurveyForPreview} from "../shared/surveyVariablePreview.util";
import {blankSurveyDraft} from "../details/surveyDraft.util";
import {PanelField} from "../editor/SurveyPanelSection";
import SurveyRunner from "../runner/SurveyRunner";
import SurveyRunnerThemeProvider from "../runner/SurveyRunnerThemeProvider";
import SurveyTemplatePicker, {BLANK_TEMPLATE_KEY} from "./SurveyTemplatePicker";

interface SurveyCreateDialogProps {
    open: boolean;
    onClose: () => void;
}

export const SURVEY_TEMPLATES_QUERY_KEY = 'survey-templates';

/** Stable empty catalogue, so a still-loading query does not hand the picker a new array every render. */
const NO_TEMPLATES: SurveyTemplateDefinition[] = [];

const SurveyCreateDialog = ({open, onClose}: SurveyCreateDialogProps) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const readOnly = useReadOnly();
    const organization = useOrganization();
    const refreshSurveys = useSurveyChangeRefresh();

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const showPreview = useMediaQuery(theme.breakpoints.up('md'));

    /**
     * The catalogue, built by the API in one language. Keyed on the interface language too: the
     * templates carry their own finished copy, so a language switch is a different catalogue rather
     * than the same one relabelled.
     */
    const {data: templates = NO_TEMPLATES, isLoading: templatesLoading} = useQuery({
        queryKey: [SURVEY_TEMPLATES_QUERY_KEY, i18n.language],
        queryFn: () => SurveyService.getTemplates(i18n.language),
        enabled: open,
        retry: false
    });

    const [selectedKey, setSelectedKey] = useState(BLANK_TEMPLATE_KEY);
    const [nameIsSuggestion, setNameIsSuggestion] = useState(true);
    const [previewLanguage, setPreviewLanguage] = useState<string | undefined>(undefined);

    const surveyData = useEntity<Survey>({
        get: async () => ({
            ...blankSurveyDraft(),
            type: SurveyType.POST_STAY,
            name: t('New empty survey'),
        }),
        /**
         * A template is created from its key, not from the previewed draft: the API mints the ids and
         * moves the visibility rules onto them, and the draft only ever held the catalogue's
         * placeholders. Everything the author can still decide here — the name — travels with it.
         */
        create: (survey) => selectedKey === BLANK_TEMPLATE_KEY
            ? SurveyService.createSurvey(survey)
            : SurveyService.instantiateTemplate({
                templateKey: selectedKey,
                language: i18n.language,
                name: survey.name,
            }),
        update: SurveyService.updateSurvey,
        isValid: (survey) => !!survey.name?.trim() && !!survey.type,
        cleanupBeforeSave: (survey) => ({...survey, name: survey.name?.trim() || undefined}),
        onSaved: (survey) => {
            void refreshSurveys();
            onClose();
            navigate(`/system/surveys/${survey.id}`);
        },
    }, "new");

    const survey = surveyData.item;

    const safeClose = () => {
        if (!surveyData.saving) onClose();
    };

    const saveNow = () => {
        if (!surveyData.isValid || readOnly || surveyData.saving) return;
        surveyData.setErrorSaving(false);
        void surveyData.save();
    };

    const nameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            nameRef.current?.focus();
            nameRef.current?.select();
        }, 250);
        return () => window.clearTimeout(timer);
    }, []);

    const nameMissing = !survey.name?.trim();

    /**
     * The type owns which templates are offered, so a template of another type cannot survive the
     * change — the draft falls back to blank, keeping only a name the author typed themselves.
     */
    const handleTypeChange = (value: string) => {
        const type = value as SurveyType;

        const selected = templates.find(entry => entry.key === selectedKey);
        if (selected && selected.survey.type !== type) {
            const keepTyped = !!survey.name?.trim() && !nameIsSuggestion;
            setSelectedKey(BLANK_TEMPLATE_KEY);
            setNameIsSuggestion(!keepTyped);
            surveyData.setItem({
                ...blankSurveyDraft(),
                name: keepTyped ? survey.name : t('New empty survey'),
                type,
            });
            return;
        }

        surveyData.setItem(previous => ({...previous, type}));
    };

    const form = (
        <Stack gap={3}>
            <Stack gap={2}>
                <PanelField
                    label={`${t('Name')} *`}
                    hint={t('Internal name of this survey, used to find it in lists and reports.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        inputRef={nameRef}
                        value={survey.name || ''}
                        onChange={(e) => {
                            const name = e.target.value;
                            setNameIsSuggestion(false);
                            surveyData.setItem(previous => ({...previous, name}));
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNow();
                        }}
                        fullWidth
                        required
                        disabled={readOnly}
                        error={nameMissing}
                        // Reserve helper-text height to avoid a validation layout shift.
                        helperText={nameMissing ? t('Name is required') : ' '}
                    />
                </PanelField>

                <PanelField
                    label={`${t('Survey type')} *`}
                    hint={t('Defines when the survey is collected and what it can be linked to.')}
                >
                    <FormControl fullWidth>
                        <Select
                            id={'type'}
                            value={survey.type ?? ''}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            size={'small'}
                            disabled={readOnly}
                        >
                            {!survey.type && (
                                <MenuItem value={''} disabled>{t('Not set')}</MenuItem>
                            )}
                            <MenuItem value={SurveyType.PRE_STAY}>{t('Pre stay')}</MenuItem>
                            <MenuItem value={SurveyType.POST_STAY}>{t('Post stay')}</MenuItem>
                            <MenuItem value={SurveyType.PRE_SERVICE}>{t('Pre service')}</MenuItem>
                            <MenuItem value={SurveyType.POST_SERVICE}>{t('Post service')}</MenuItem>
                            <MenuItem value={SurveyType.ANONYMOUS}>{t('Anonymous')}</MenuItem>
                        </Select>
                    </FormControl>
                </PanelField>
            </Stack>

            <SurveyTemplatePicker
                survey={survey}
                onChange={surveyData.setItem}
                templates={templates}
                selectedKey={selectedKey}
                onSelectedKeyChange={setSelectedKey}
                nameIsSuggestion={nameIsSuggestion}
                onNameIsSuggestionChange={setNameIsSuggestion}
                filterType={survey.type}
                disabled={surveyData.saving || readOnly || templatesLoading}
            />
        </Stack>
    );

    const previewQuestions = useMemo(
        () => resolveSurveyForPreview({questions: survey.questions}).questions,
        [survey.questions]
    );
    const previewSurvey: Survey = {...survey, questions: previewQuestions};

    const offeredPreviewLanguage = previewLanguage && survey.languages?.includes(previewLanguage)
        ? previewLanguage
        : undefined;

    const formColumn = (
        <Box
            sx={{
                flex: showPreview ? '1 1 45%' : 1,
                minWidth: 0,
                overflowY: 'auto',
                py: 3,
                pr: showPreview ? 1 : 0,
            }}
        >
            {form}
        </Box>
    );

    const previewColumn = (
        <Box sx={{flex: '1 1 55%', minWidth: 0, py: 3, display: 'flex', flexDirection: 'column'}}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <SurveyRunnerThemeProvider key={selectedKey} theme={survey.theme}>
                    <SurveyRunner
                        survey={previewSurvey}
                        language={offeredPreviewLanguage}
                        onLanguageChange={setPreviewLanguage}
                        organizationName={organization?.name}
                    />
                </SurveyRunnerThemeProvider>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={safeClose}
            maxWidth={'lg'}
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{sx: fullScreen ? {} : {height: '85vh', maxHeight: '85vh'}}}
            aria-labelledby={'create-survey-dialog-title'}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography id={'create-survey-dialog-title'} variant={'h6'} fontWeight={600} noWrap>
                        {t('Create survey')}
                    </Typography>
                    <IconButton onClick={safeClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                {showPreview
                    ? (
                        <Stack direction={'row'} gap={3} sx={{flex: 1, minHeight: 0, minWidth: 0}}>
                            {formColumn}
                            {previewColumn}
                        </Stack>
                    )
                    : formColumn}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={safeClose} disabled={surveyData.saving}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    id={'btn-entity-save'}
                    variant={'contained'}
                    onClick={saveNow}
                    loading={surveyData.saving}
                    disabled={!surveyData.isValid || readOnly}
                >
                    {t('Create survey')}
                </LoadingButton>

                {surveyData.renderSnackbars()}
            </DialogActions>
        </Dialog>
    );
};

export default SurveyCreateDialog;
